import { type CSSProperties, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import type { ApiClient } from '@sentinel/api-client';
import { clearPendingRequest, STEP_UP_FEDERATED_EVENT } from '@sentinel/api-client';
import { Button, DialogShell, sentinel } from '@sentinel/ui';

// Re-export the canonical event name (defined in api-client) for app convenience.
export { STEP_UP_FEDERATED_EVENT };

export interface FederatedStepUpRequiredEventDetail {
  /** SPA path/URL to return to after the IdP round-trip. */
  returnTo: string;
}

declare global {
  interface WindowEventMap {
    'sentinel:step-up-federated-required': CustomEvent<FederatedStepUpRequiredEventDetail>;
  }
}

export interface FederatedStepUpMessages {
  title: string;
  /** Build the body copy; `idpName` is null until resolved or on failure. */
  describe: (idpName: string | null) => string;
  confirm: string;
  cancel: string;
  /** Optional label rendered above the destination affordance (e.g. "Redirecting to"). */
  destinationLabel?: string;
}

export interface FederatedStepUpProviderProps {
  apiClient: ApiClient;
  messages: FederatedStepUpMessages;
  /** Full-page navigation. Defaults to window.location.assign. */
  navigate?: (url: string) => void;
  /** Clear the persisted pending request on cancel. Defaults to api-client's. */
  clearPending?: () => void;
  children?: ReactNode;
}

// Inset "destination" affordance: communicates the external IdP hand-off with
// a leading accent stripe and a redirect glyph. Refined, not loud. Authored as
// inline styles (matching the sibling dialog.tsx) so auth-core needs no direct
// StyleX dependency; colour/spacing values come from the sentinel design tokens.
const styles: Record<string, CSSProperties> = {
  destination: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.625rem',
    marginTop: '0.25rem',
    padding: '0.625rem 0.75rem',
    borderRadius: '0.5rem',
    borderInlineStart: `2px solid ${sentinel.accentAction}`,
    backgroundColor: sentinel.surfaceSunken,
  },
  arrow: {
    flexShrink: 0,
    fontSize: '1rem',
    lineHeight: 1,
    color: sentinel.accentAction,
  },
  destinationText: {
    display: 'grid',
    gap: '0.125rem',
    minWidth: 0,
  },
  destinationLabel: {
    margin: 0,
    fontSize: '0.6875rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: sentinel.textMuted,
  },
  idpName: {
    margin: 0,
    fontSize: '0.875rem',
    fontWeight: 600,
    color: sentinel.textBody,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
};

/**
 * Listens for `sentinel:step-up-federated-required` window events fired by the
 * api-client middleware when an action needs a fresh re-authentication at the
 * user's federated IdP. Renders a confirmation prompt; on Continue it performs
 * a full-page navigation to `/sso/step-up/initiate`, on Cancel it clears the
 * persisted pending request so the original 403 surfaces to the caller.
 */
export function FederatedStepUpProvider({
  apiClient,
  messages,
  navigate,
  clearPending,
  children,
}: FederatedStepUpProviderProps) {
  const [returnTo, setReturnTo] = useState<string | null>(null);
  const [idpName, setIdpName] = useState<string | null>(null);
  const open = returnTo !== null;
  const fetchSeqRef = useRef(0);

  const doNavigate =
    navigate ??
    ((u: string) => {
      window.location.assign(u);
    });
  const doClear = clearPending ?? clearPendingRequest;

  useEffect(() => {
    function onEvent(e: WindowEventMap['sentinel:step-up-federated-required']) {
      const detail = e.detail;
      if (!detail || typeof detail.returnTo !== 'string') return;
      setIdpName(null);
      setReturnTo(detail.returnTo);
      const seq = ++fetchSeqRef.current;
      void (async () => {
        try {
          const res = await apiClient.GET('/sso/step-up/info');
          if (seq === fetchSeqRef.current && res.data?.idp_name) {
            setIdpName(res.data.idp_name);
          }
        } catch {
          // Leave generic copy; the prompt still works without a name.
        }
      })();
    }
    window.addEventListener(STEP_UP_FEDERATED_EVENT, onEvent);
    return () => window.removeEventListener(STEP_UP_FEDERATED_EVENT, onEvent);
  }, [apiClient]);

  const handleConfirm = useCallback(() => {
    if (returnTo === null) return;
    const target = `/sso/step-up/initiate?return_to=${encodeURIComponent(returnTo)}`;
    setReturnTo(null);
    doNavigate(target);
  }, [returnTo, doNavigate]);

  const handleCancel = useCallback(() => {
    doClear();
    setReturnTo(null);
  }, [doClear]);

  return (
    <>
      {children}
      <DialogShell
        open={open}
        onCancel={handleCancel}
        title={messages.title}
        description={messages.describe(idpName)}
        width="narrow"
        testId="federated-step-up-dialog"
      >
        {idpName ? (
          <div style={styles.destination} data-testid="federated-step-up-destination">
            <span style={styles.arrow} aria-hidden="true">
              &#8599;
            </span>
            <span style={styles.destinationText}>
              <span style={styles.destinationLabel}>
                {messages.destinationLabel ?? 'Redirecting to'}
              </span>
              <span style={styles.idpName}>{idpName}</span>
            </span>
          </div>
        ) : null}
        <DialogShell.Footer>
          <Button
            type="button"
            variant="secondary"
            onClick={handleCancel}
            data-testid="federated-step-up-cancel"
          >
            {messages.cancel}
          </Button>
          <Button type="button" onClick={handleConfirm} data-testid="federated-step-up-confirm">
            {messages.confirm}
          </Button>
        </DialogShell.Footer>
      </DialogShell>
    </>
  );
}
